import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'
import { useSchedulerHost } from '../context/SchedulerHostContext'
import { ganttLinkOf, hasSignalAction, withSiteId, type SignalAction } from '../constants/signalView'
import type { ParsedDeepLink } from '../constants/deepLink'
import { useInvalidateSignals } from './useTaskSignals'

// 신호 팝오버 · 오늘 할일 패널의 처리 버튼 — 서버(SIG-01 · TODO-01)가 내려준 주소로만 처리한다(화면이 할일 종류로 행선지를 고르지 않음).
//  · 동작 유형 API  → actionUrl 을 호출한 뒤 신호 · 할일을 다시 조회한다
//  · 동작 유형 ROUTE → webPath 가 간트 주소면 화면이 그 자리에서 연다(openGanttLink), 아니면 호스트가 그 주소로 이동한다(openPath)

/**
 * @param openGanttLink 간트 주소를 이 화면이 그 자리에서 열 수 있으면 열고 true. 못 열면 false(호스트 이동으로 넘김)
 * @param defaultSiteId 이 화면이 보고 있는 현장 — 처리 항목이 현장을 싣지 않을 때(신호 팝오버) 작업보고 확인 주소에 붙인다(withSiteId)
 */
export function useSignalAction(openGanttLink?: (link: ParsedDeepLink, path: string) => boolean, defaultSiteId?: number | null) {
  const host = useSchedulerHost()
  const invalidateSignals = useInvalidateSignals()
  const user = useAuthStore(s => s.user)

  /** 버튼을 보일지 — 버튼 문구 · 주소가 있고, 간트가 아닌 주소면 호스트 이동이 주입돼 있을 때 */
  const canAct = (a: SignalAction): boolean => {
    if (!hasSignalAction(a)) return false
    if (a.actionTypeCode === 'API') return true
    return (!!openGanttLink && ganttLinkOf(a.webPath) != null) || !!host?.openPath
  }

  const act = (a: SignalAction & { siteId?: number | null }): void => {
    if (a.actionTypeCode === 'API') {
      if (!a.actionUrl) return
      apiClient.post(a.actionUrl, null, {
        headers: {
          ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}),
          ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
        },
      })
        .then(() => { void invalidateSignals() })
        .catch(() => toast.error('처리하지 못했어요. 잠시 후 다시 시도해 주세요.'))
      return
    }
    const path = a.webPath
    if (!path) return
    const link = ganttLinkOf(path)
    if (link && openGanttLink?.(link, path)) return
    host?.openPath?.(withSiteId(path, a.siteId ?? defaultSiteId))
  }

  return { canAct, act }
}
